import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { User } from "types";

export type CreateEmploymentHistoryPayload = {
  startDate: string;
  endDate: string;
  position: string;
  salaryAmount: number;
  salaryType: "annual" | "daily" | "hourly" | "biweekly";
  currency: string;
};

export type EmploymentHistoryCreateResponse = {
  success: boolean;
  message: string;
  data: User;
};

const createEmploymentHistory = async (
  userId: string,
  payload: CreateEmploymentHistoryPayload
): Promise<EmploymentHistoryCreateResponse> => {
  const { data } = await customAxios.post<EmploymentHistoryCreateResponse>(
    `/users/${userId}/employment-history`,
    payload
  );
  return data;
};

export const useCreateEmploymentHistory = () => {
  const queryClient = useQueryClient();

  return useMutation<
    EmploymentHistoryCreateResponse,
    AxiosError,
    { userId: string; payload: CreateEmploymentHistoryPayload }
  >(({ userId, payload }) => createEmploymentHistory(userId, payload), {
    onSuccess: async (data, variables) => {
      showNotification({
        title: "Success",
        message: data.message ?? "Employment history added successfully",
        color: "green",
      });

      await queryClient.invalidateQueries(["users"]);
      await queryClient.invalidateQueries(["user", variables.userId]);
      await queryClient.invalidateQueries([
        "employmentHistory",
        variables.userId,
      ]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to add employment history",
        color: "red",
      }),
  });
};
